import { TrendingUp } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

// --- Operator / company information ---------------------------------------
// Single source of truth for the legal-entity block rendered at the top of
// every legal page (Privacy / Terms / GDPR / Cookies). Required for EU
// affiliate network onboarding (AffiliatePort.eu et al.) and GDPR Art. 13.
const COMPANY = {
  name: "RESTOB s.r.o.",
  address_street: "Kopčianska 10",
  address_city: "851 01 Bratislava",
  address_country: "Slovensko",
  ico: "50538381",
  dic: "2120374344",
  ic_dph: "SK2120374344",
  privacy_email: "info@toprankpulse.com",
  support_email: "support@toprankpulse.com",
  product: "TopRankPulse",
  domain: "toprankpulse.com",
};

const SECTION_KEYS = {
  privacy: {
    titleKey: "priv_title",
    sections: Array.from({ length: 10 }, (_, i) => ({ hKey: `priv_${i + 1}h`, tKey: `priv_${i + 1}t` })),
  },
  terms: {
    titleKey: "terms_title",
    sections: Array.from({ length: 10 }, (_, i) => ({ hKey: `terms_${i + 1}h`, tKey: `terms_${i + 1}t` })),
  },
  gdpr: {
    titleKey: "gdpr_title",
    sections: Array.from({ length: 6 }, (_, i) => ({ hKey: `gdpr_${i + 1}h`, tKey: `gdpr_${i + 1}t` })),
  },
  cookies: {
    titleKey: "cookie_title",
    // Cookie policy now has 7 entries (was 3) — meets the GDPR cookie-banner
    // disclosure standard expected by EU networks: identify each cookie by
    // name, purpose, duration, and processor.
    sections: Array.from({ length: 7 }, (_, i) => ({ hKey: `cookie_${i + 1}h`, tKey: `cookie_${i + 1}t` })),
  },
};

// EN fallback content for languages that only have titles translated
const EN_FALLBACK = {
  priv_1h: "1. Data Controller",
  priv_1t: `The data controller for personal data processed through ${COMPANY.domain} is ${COMPANY.name}, registered at ${COMPANY.address_street}, ${COMPANY.address_city}, ${COMPANY.address_country}. Company ID (IČO): ${COMPANY.ico}. Tax ID (DIČ): ${COMPANY.dic}. VAT ID (IČ DPH): ${COMPANY.ic_dph}.`,
  priv_2h: "2. Data We Collect",
  priv_2t: "We collect: email address (registration), website URLs (scanning), usage data (analytics), IP addresses (security), and limited browser fingerprint (fraud prevention). We do NOT collect payment card data — all payments are processed by Stripe under their own privacy policy.",
  priv_3h: "3. Purpose of Processing",
  priv_3t: "Your data is used to: provide SEO/AI-visibility analysis, send transactional emails, prevent service abuse, improve our products, and comply with legal obligations. Legal bases: consent (Art. 6(1)(a) GDPR), contract performance (Art. 6(1)(b)), legitimate interest (Art. 6(1)(f)), and legal obligation (Art. 6(1)(c)).",
  priv_4h: "4. Data Retention",
  priv_4t: "Account data is retained while your account remains active plus 30 days after closure. Scan/report data: 12 months. Security logs: 90 days. Invoice records: 10 years (Slovak Act 431/2002 on Accounting). You may request deletion at any time.",
  priv_5h: "5. Your Rights (GDPR)",
  priv_5t: `You have the right to access, rectify, erase, restrict processing, data portability, object to processing, and withdraw consent. To exercise any right, write to ${COMPANY.privacy_email} — we respond within 30 days.`,
  priv_6h: "6. Cookies",
  priv_6t: "We use essential cookies (session, language preference, fraud-prevention) and optional analytics cookies. Full breakdown on our Cookie Policy page. You can manage consent in your browser and via our cookie banner.",
  priv_7h: "7. Third-Party Processors",
  priv_7t: "We share data with: Stripe (payments — Ireland/USA), MongoDB Atlas (database — EU region), Resend (transactional email — USA), OpenAI / Anthropic / Google (AI analysis — USA), Cloudflare (CDN/security — EU edge). Each is bound by a Data Processing Agreement under Art. 28 GDPR.",
  priv_8h: "8. Data Security",
  priv_8t: "We implement: HTTPS-only transport, JWT short-lived tokens, password hashing (bcrypt), rate limiting, brute-force lockout, IP-based abuse detection, security headers (CSP, HSTS, X-Frame-Options), input sanitization, and regular dependency audits.",
  priv_9h: "9. International Transfers",
  priv_9t: "Where data leaves the EEA (e.g. Stripe, OpenAI), transfers are protected by the EU Standard Contractual Clauses (SCCs) and, where applicable, the EU-US Data Privacy Framework adequacy decision.",
  priv_10h: "10. Contact & Complaints",
  priv_10t: `Privacy inquiries: ${COMPANY.privacy_email}. You also have the right to lodge a complaint with the Slovak supervisory authority — Úrad na ochranu osobných údajov SR, Hraničná 12, 820 07 Bratislava, statny.dozor@pdp.gov.sk.`,

  terms_1h: "1. Agreement",
  terms_1t: `By accessing ${COMPANY.domain} or using any service operated by ${COMPANY.name}, you agree to these Terms. If you do not agree, do not use our services.`,
  terms_2h: "2. Services",
  terms_2t: "TopRankPulse provides SEO/GEO analysis, AI-visibility tracking, citation scanning, blog and ad generation, and related marketing-automation tools. The free tier is intentionally limited (1 scan/month, redacted citations). Paid tiers unlock additional features.",
  terms_3h: "3. Account",
  terms_3t: "You must provide accurate information when registering. You are responsible for maintaining the confidentiality of your credentials and for all activity under your account.",
  terms_4h: "4. Pricing, Payments & VAT",
  terms_4t: `All prices are net amounts in EUR / USD as shown at checkout. As ${COMPANY.name} is a Slovak VAT-registered entity (${COMPANY.ic_dph}), VAT is added per applicable EU rules (reverse-charge for B2B in EU, Slovak VAT for B2C in SK, 0% for non-EU B2C). Subscriptions renew automatically and may be cancelled at any time from the dashboard.`,
  terms_5h: "5. Refunds & Money-Back Guarantee",
  terms_5t: "We offer a 14-day money-back guarantee on first-time purchases — write to support@toprankpulse.com within 14 days of payment and we refund in full, no questions asked. Recurring renewals are non-refundable but may be cancelled before the next billing cycle.",
  terms_6h: "6. Fair Use",
  terms_6t: "You may not: use automated scripts to abuse the service, resell raw scan data, reverse-engineer our algorithms, scan sites you do not own or have explicit permission for, or use the service for any unlawful purpose.",
  terms_7h: "7. Intellectual Property",
  terms_7t: "TopRankPulse software, design, brand and content are owned by RESTOB s.r.o. Reports generated for your own websites remain your property and may be used freely. Aggregated, anonymised performance statistics may be used by us for product improvement and marketing.",
  terms_8h: "8. Limitation of Liability",
  terms_8t: "The service is provided 'as is'. We do not guarantee specific SEO results, search-engine rankings or AI citations. Maximum aggregate liability is limited to the amount you paid in the 12 months preceding the claim.",
  terms_9h: "9. Termination",
  terms_9t: "We may suspend or terminate accounts that materially violate these terms or applicable law. You may delete your account at any time from the dashboard or by emailing support.",
  terms_10h: "10. Governing Law & Jurisdiction",
  terms_10t: "These terms are governed by the laws of the Slovak Republic. Any dispute that cannot be resolved amicably shall be settled by the competent courts of Bratislava, Slovakia. Consumers retain the protections of mandatory law in their country of residence.",

  gdpr_1h: "Data Protection Officer",
  gdpr_1t: `For all data-protection matters write to ${COMPANY.privacy_email}. Postal: ${COMPANY.name}, ${COMPANY.address_street}, ${COMPANY.address_city}, ${COMPANY.address_country}.`,
  gdpr_2h: "Lawful Basis (Art. 6 GDPR)",
  gdpr_2t: "We process personal data on the bases of: explicit consent, contractual necessity, legitimate interest (e.g. fraud prevention), and legal obligation (e.g. accounting).",
  gdpr_3h: "Data Subject Rights",
  gdpr_3t: "Right to access (Art. 15), rectification (Art. 16), erasure (Art. 17), restriction (Art. 18), portability (Art. 20), objection (Art. 21), and to withdraw consent at any time without affecting the lawfulness of prior processing.",
  gdpr_4h: "Breach Notification",
  gdpr_4t: "Where a personal-data breach is likely to result in a high risk to your rights and freedoms, we will notify you without undue delay and inform the Slovak supervisory authority within 72 hours, in line with Art. 33 and Art. 34 GDPR.",
  gdpr_5h: "Sub-Processors & DPAs",
  gdpr_5t: "We maintain Data Processing Agreements (Art. 28 GDPR) with every sub-processor: Stripe (payments), MongoDB Atlas (database), Resend (email), OpenAI / Anthropic / Google (AI inference), Cloudflare (CDN). The current list is available on request.",
  gdpr_6h: "Privacy by Design",
  gdpr_6t: "Privacy by Design and by Default is embedded in our SDLC: minimal-collection defaults, role-based access control, audit logging, encrypted storage, and quarterly security reviews.",

  cookie_1h: "Strictly Necessary",
  cookie_1t: "access_token / refresh_token (httpOnly · session+7 days · authentication), language (1 year · UI language), trp_session_id (30 days · anti-CSRF and behavior analytics keyed to opaque ID). Cannot be disabled; the service cannot function without them.",
  cookie_2h: "Functional",
  cookie_2t: "theme (1 year · light/dark preference), trp_aff_code (90 days · affiliate attribution, set when arriving via /r/:code), pwa_install_dismissed (180 days · UI prompt state).",
  cookie_3h: "Analytics",
  cookie_3t: "When you accept the analytics cookie banner we set _ga / _ga_* (Google Analytics 4 — 2 years, anonymised IP, no cross-site tracking). You may revoke consent at any time via the cookie banner.",
  cookie_4h: "Payment",
  cookie_4t: "Stripe sets its own cookies (__stripe_mid, __stripe_sid) for fraud detection during checkout. These are governed by Stripe's privacy policy (stripe.com/privacy).",
  cookie_5h: "Security / Fraud Prevention",
  cookie_5t: "Cloudflare sets __cf_bm (30 min · bot management) and cf_clearance (1 year · DDoS challenge solution) on edge requests. Strictly necessary and cannot be opted out without losing access to the site.",
  cookie_6h: "Third-Party Embeds",
  cookie_6t: "If you watch an embedded video or use a social-share widget, the relevant provider (YouTube, X, LinkedIn) may set its own cookies. We do not control these — refer to the provider's policy.",
  cookie_7h: "Managing Cookies",
  cookie_7t: "You can withdraw consent at any time via the cookie banner (footer · 'Cookie settings') or by clearing cookies in your browser. Disabling strictly-necessary cookies will break authentication and may prevent the site from loading.",
};

export default function LegalPage() {
  const { t } = useLanguage();
  const page = window.location.pathname.replace("/", "");
  const config = SECTION_KEYS[page] || SECTION_KEYS.privacy;

  const title = t[config.titleKey] || EN_FALLBACK[config.titleKey] || config.titleKey;
  const sections = config.sections
    .map(s => ({
      h: t[s.hKey] || EN_FALLBACK[s.hKey] || "",
      t: t[s.tKey] || EN_FALLBACK[s.tKey] || "",
    }))
    .filter(s => s.h);

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-white">
      <header className="bg-[var(--color-bg)]/80 backdrop-blur-xl border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2" data-testid="legal-home-link">
            <div className="w-7 h-7 bg-[#a855f7] flex items-center justify-center"><TrendingUp className="w-4 h-4 text-white" /></div>
            <span className="text-lg font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
          </a>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="text-3xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-2" data-testid="legal-title">{title}</h1>
        <p className="text-[var(--color-text-muted)] text-sm font-['JetBrains_Mono'] mb-10">{t.legal_updated || "Last updated: May 2026"}</p>

        {/* Operator / company block — required for EU compliance + affiliate
            network onboarding. Mirrors the SK §3 Občianskeho zákonníka
            "Impressum" requirement and §6 of Slovak Act 22/2004 on
            electronic commerce. */}
        <div
          data-testid="legal-operator-block"
          className="mb-12 p-5 bg-white/[0.03] border border-white/10 rounded-lg"
        >
          <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--color-text-muted)] mb-3 font-['JetBrains_Mono']">
            Prevádzkovateľ / Operator
          </p>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <Row label="Company" value={COMPANY.name} testid="op-name" />
            <Row label="Product" value={`${COMPANY.product} (${COMPANY.domain})`} testid="op-product" />
            <Row label="Address" value={`${COMPANY.address_street}, ${COMPANY.address_city}, ${COMPANY.address_country}`} testid="op-address" />
            <Row label="IČO" value={COMPANY.ico} testid="op-ico" mono />
            <Row label="DIČ" value={COMPANY.dic} testid="op-dic" mono />
            <Row label="IČ DPH (VAT)" value={COMPANY.ic_dph} testid="op-vat" mono />
            <Row label="Privacy contact" value={COMPANY.privacy_email} testid="op-privacy-email" mono />
            <Row label="Support contact" value={COMPANY.support_email} testid="op-support-email" mono />
          </dl>
        </div>

        <div className="space-y-8">
          {sections.map((s, i) => (
            <div key={i} data-testid={`legal-section-${i + 1}`}>
              <h2 className="text-lg font-bold font-['Cabinet_Grotesk'] text-[var(--color-text)] mb-2">{s.h}</h2>
              <p className="text-[var(--color-text-muted)] text-sm leading-relaxed whitespace-pre-line">{s.t}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-[var(--color-border)]">
          <p className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono']" data-testid="legal-footer">
            {COMPANY.name} · {COMPANY.product} · {COMPANY.privacy_email}
          </p>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, testid, mono }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3">
      <dt className="text-[11px] uppercase tracking-wider text-[var(--color-text-muted)] sm:w-28 shrink-0 font-['JetBrains_Mono']">{label}</dt>
      <dd
        data-testid={testid}
        className={`text-[var(--color-text)] ${mono ? "font-['JetBrains_Mono'] text-[13px]" : ""}`}
      >
        {value}
      </dd>
    </div>
  );
}
